import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CalendarFeedDto,
  ExportTokenResponse,
  HouseholdPersonDto,
  MealPlaceDto,
  MealSlot,
} from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { ArrowRightIcon, BellIcon, ClockIcon, UsersIcon } from '../../components/icons';
import { PlanIcon } from '../../components/NavIcons';
import { Section } from '../../components/Section';
import { SelectField } from '../../components/SelectField';
import { SettingsNav } from '../../components/SettingsNav';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import {
  cellExtraSummary,
  cellKey,
  extrasFromRows,
  profileKeys,
  sortPlaces,
  withExtras,
  type CellExtra,
  type CellExtras,
} from '../../lib/profiles';
import {
  fromWeekGrid,
  scheduleKeys,
  syncedAgo,
  toWeekGrid,
  WEEKDAY_LABEL,
  WEEKDAY_ORDER,
  type WeekGrid,
} from '../../lib/schedule';

const EXTRA_SLOTS: { slot: MealSlot; label: string }[] = [
  { slot: 'breakfast', label: 'Breakfast' },
  { slot: 'lunch', label: 'Lunch' },
];

const smallButton =
  'inline-flex min-h-9 items-center justify-center rounded-lg border border-line bg-surface px-3 text-sm font-medium text-brand hover:bg-brand-soft disabled:opacity-50';

/** "6:00 PM" from `HH:MM` in the browser's locale. */
function clock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Settings → Meal times & schedule (D27/D28): when the household eats, its
 * weekly busy times, the thaw reminder lead time, imported calendars, and
 * the export feed for Google Calendar.
 */
export function SchedulePage() {
  const { auth } = useAuth();
  const canManage = auth.status === 'authed' && (auth.role === 'owner' || auth.role === 'adult');
  return (
    <AppShell title="Meal times & schedule">
      <SettingsNav />
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          FewerBags uses this to time meals, leave enough cooking time on busy evenings, and remind
          you to take things out of the freezer.
        </p>
        {canManage ? null : (
          <Alert>Only owners and adults in the household can change these settings.</Alert>
        )}
        <MealTimesSection canManage={canManage} />
        <BusyBlocksSection canManage={canManage} />
        <ThawSection canManage={canManage} />
        <CalendarsSection canManage={canManage} />
        <ExportSection canManage={canManage} />
      </div>
    </AppShell>
  );
}

// ------------------------------------------------------------- meal times --

function MealTimesSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: scheduleKeys.mealTimes,
    queryFn: () => api.schedule.mealTimes(),
  });
  const [grid, setGrid] = useState<WeekGrid>({});
  const [showExtra, setShowExtra] = useState(false);
  const [extras, setExtras] = useState<CellExtras>({});
  const [cell, setCell] = useState<{ weekday: number; slot: MealSlot; label: string } | null>(null);
  const places = useQuery({
    queryKey: profileKeys.places,
    queryFn: () => api.households.mealPlaces.list(),
  });
  const people = useQuery({
    queryKey: queryKeys.people,
    queryFn: () => api.households.people.list(),
  });
  const placeList = sortPlaces(places.data?.places ?? []);
  const personList = people.data?.people ?? [];
  const placeName = (id: string) => placeList.find((p) => p.id === id)?.name;
  const personName = (id: string) => personList.find((p) => p.id === id)?.name;
  useEffect(() => {
    if (!query.data) return;
    setGrid(toWeekGrid(query.data.mealTimes));
    setExtras(extrasFromRows(query.data.mealTimes));
    setShowExtra(query.data.mealTimes.some((m) => m.slot !== 'dinner'));
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      api.schedule.replaceMealTimes({ mealTimes: withExtras(fromWeekGrid(grid), extras) }),
    onSuccess: (data) => qc.setQueryData(scheduleKeys.mealTimes, data),
  });

  const setTime = (weekday: number, slot: MealSlot, value: string) => {
    save.reset();
    setGrid((g) => ({ ...g, [weekday]: { ...g[weekday], [slot]: value } }));
  };
  const setExtra = (weekday: number, slot: MealSlot, extra: CellExtra) => {
    save.reset();
    setExtras((x) => ({ ...x, [cellKey(weekday, slot)]: extra }));
  };
  const copyMonday = () => {
    save.reset();
    setGrid((g) => Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((d) => [d, { ...g[1] }])));
  };
  const slots: { slot: MealSlot; label: string }[] = [
    ...(showExtra ? EXTRA_SLOTS : []),
    { slot: 'dinner', label: 'Dinner' },
  ];

  return (
    <Section
      icon={<ClockIcon className="size-5" />}
      title="When you eat"
      description="The usual time each meal is on the table. Leave a day blank if you don’t normally eat that meal. Use … to set where it’s eaten and who eats it."
    >
      {query.isError ? <Alert tone="error">{messageForError(query.error)}</Alert> : null}
      {query.data?.isDefault ? (
        <Alert>These are the starting defaults (dinner at {clock('18:00')} every day).</Alert>
      ) : null}
      <form
        className="flex flex-col gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-1 pr-2 font-medium">Day</th>
                {slots.map((s) => (
                  <th key={s.slot} className="py-1 pr-2 font-medium">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WEEKDAY_ORDER.map((d) => (
                <tr key={d} className="border-t border-line">
                  <th scope="row" className="py-1.5 pr-2 text-left font-medium text-ink">
                    {WEEKDAY_LABEL[d]}
                  </th>
                  {slots.map((s) => {
                    const summary = cellExtraSummary(
                      extras[cellKey(d, s.slot)],
                      placeName,
                      personName,
                    );
                    const label = `${s.label} on ${WEEKDAY_LABEL[d]}`;
                    return (
                      <td key={s.slot} className="py-1.5 pr-2 align-top">
                        <div className="flex items-center gap-1">
                          <input
                            type="time"
                            aria-label={label}
                            value={grid[d]?.[s.slot] ?? ''}
                            onChange={(e) => setTime(d, s.slot, e.target.value)}
                            disabled={!canManage}
                            className="min-h-11 w-full min-w-[7rem] rounded-lg border border-line bg-surface px-2 text-ink"
                          />
                          <button
                            type="button"
                            aria-label={`Where and who: ${label}`}
                            title="Where and who"
                            disabled={!grid[d]?.[s.slot]}
                            onClick={() => setCell({ weekday: d, slot: s.slot, label })}
                            className={`inline-flex size-11 shrink-0 items-center justify-center rounded-lg border text-base font-semibold disabled:opacity-40 ${
                              summary
                                ? 'border-brand bg-brand-soft text-brand'
                                : 'border-line bg-surface text-muted hover:text-brand'
                            }`}
                          >
                            <span aria-hidden>…</span>
                          </button>
                        </div>
                        {summary && grid[d]?.[s.slot] ? (
                          <p className="mt-0.5 max-w-[10rem] truncate text-xs text-muted">
                            {summary}
                          </p>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showExtra}
              onChange={(e) => setShowExtra(e.target.checked)}
              className="size-4 accent-brand"
            />
            Also set breakfast and lunch
          </label>
          {canManage ? (
            <button type="button" className={smallButton} onClick={copyMonday}>
              Use Monday’s times every day
            </button>
          ) : null}
        </div>
        {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
        {save.isSuccess ? <Alert tone="success">Meal times saved.</Alert> : null}
        {canManage ? (
          <Button type="submit" disabled={save.isPending || query.isPending}>
            Save meal times
          </Button>
        ) : null}
      </form>
      {cell ? (
        <MealCellDialog
          title={cell.label}
          extra={extras[cellKey(cell.weekday, cell.slot)]}
          places={placeList}
          people={personList}
          canManage={canManage}
          onClose={() => setCell(null)}
          onChange={(extra) => setExtra(cell.weekday, cell.slot, extra)}
        />
      ) : null}
    </Section>
  );
}

/** Per-cell "…" editor: where the meal is eaten and who usually eats it (D32). */
function MealCellDialog({
  title,
  extra,
  places,
  people,
  canManage,
  onClose,
  onChange,
}: {
  title: string;
  extra: CellExtra | undefined;
  places: readonly MealPlaceDto[];
  people: readonly HouseholdPersonDto[];
  canManage: boolean;
  onClose: () => void;
  onChange: (extra: CellExtra) => void;
}) {
  const current: CellExtra = extra ?? { placeId: null, attendeePersonIds: [] };
  const home = places.find((p) => p.kind === 'home');
  const everyone = current.attendeePersonIds.length === 0;
  const toggle = (id: string) =>
    onChange({
      ...current,
      attendeePersonIds: current.attendeePersonIds.includes(id)
        ? current.attendeePersonIds.filter((x) => x !== id)
        : [...current.attendeePersonIds, id],
    });
  return (
    <Dialog open onClose={onClose} title={title}>
      <SelectField
        id="meal-cell-place"
        label="Where"
        value={current.placeId ?? home?.id ?? ''}
        disabled={!canManage}
        options={
          places.length
            ? places.map((p) => ({ value: p.id, label: p.name }))
            : [{ value: '', label: 'Home' }]
        }
        onChange={(e) => {
          const id = e.target.value;
          onChange({ ...current, placeId: !id || id === home?.id ? null : id });
        }}
      />
      <fieldset className="flex flex-col gap-2" disabled={!canManage}>
        <legend className="text-sm font-medium text-ink">Who eats</legend>
        <p className="-mt-1 text-xs text-muted">
          {everyone ? 'Everyone in the household.' : 'Only the people picked.'} Servings follow this
          when a meal is planned.
        </p>
        <ul className="flex flex-wrap gap-2">
          <li>
            <button
              type="button"
              aria-pressed={everyone}
              onClick={() => onChange({ ...current, attendeePersonIds: [] })}
              className={`min-h-11 rounded-full border px-3.5 text-sm ${
                everyone ? 'border-brand bg-brand text-white' : 'border-line bg-surface text-ink'
              }`}
            >
              Everyone
            </button>
          </li>
          {people.map((p) => {
            const on = current.attendeePersonIds.includes(p.id);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(p.id)}
                  className={`min-h-11 rounded-full border px-3.5 text-sm ${
                    on ? 'border-brand bg-brand text-white' : 'border-line bg-surface text-ink'
                  }`}
                >
                  {on ? <span aria-hidden>✓ </span> : null}
                  {p.name}
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>
      <p className="text-xs text-muted">Changes are saved with “Save meal times”.</p>
      <Button variant="secondary" onClick={onClose}>
        Done
      </Button>
    </Dialog>
  );
}

// ------------------------------------------------------------ busy blocks --

function BusyBlocksSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const blocks = useQuery({
    queryKey: scheduleKeys.busyBlocks,
    queryFn: () => api.schedule.busyBlocks(),
  });
  const members = useQuery({
    queryKey: queryKeys.members,
    queryFn: () => api.households.members.list(),
  });
  const memberName = new Map(
    (members.data?.members ?? []).map((m) => [m.user.id, m.user.displayName]),
  );
  const invalidate = () => qc.invalidateQueries({ queryKey: scheduleKeys.busyBlocks });

  const add = useMutation({
    mutationFn: (input: Parameters<typeof api.schedule.createBusyBlock>[0]) =>
      api.schedule.createBusyBlock(input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.schedule.removeBusyBlock(id),
    onSuccess: invalidate,
  });

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const userId = String(fd.get('userId') ?? '');
    add.mutate(
      {
        label: String(fd.get('label') ?? ''),
        weekday: Number(fd.get('weekday')),
        startTime: String(fd.get('startTime') ?? ''),
        endTime: String(fd.get('endTime') ?? ''),
        userId: userId || null,
      },
      { onSuccess: () => form.reset() },
    );
  }

  const list = blocks.data?.busyBlocks ?? [];
  return (
    <Section
      icon={<UsersIcon className="size-5" />}
      title="Busy every week"
      description="Regular commitments around dinner time — practice, lessons, late shifts. The week planner picks quicker recipes on those days."
    >
      {blocks.isError ? <Alert tone="error">{messageForError(blocks.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
      {blocks.isSuccess && list.length === 0 ? (
        <p className="text-sm text-muted">Nothing yet.</p>
      ) : null}
      {list.length ? (
        <ul className="flex flex-col divide-y divide-line">
          {list.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{b.label}</p>
                <p className="text-xs text-muted">
                  {WEEKDAY_LABEL[b.weekday]} · {clock(b.startTime)}–{clock(b.endTime)}
                  {b.endTime <= b.startTime ? ' (next day)' : ''}
                  {b.userId && memberName.get(b.userId) ? ` · ${memberName.get(b.userId)}` : ''}
                </p>
              </div>
              {canManage ? (
                <button
                  type="button"
                  className={`${smallButton} text-fail`}
                  onClick={() => remove.mutate(b.id)}
                  disabled={remove.isPending}
                  aria-label={`Remove ${b.label} on ${WEEKDAY_LABEL[b.weekday]}`}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {canManage ? (
        <form onSubmit={onAdd} className="flex flex-col gap-3 rounded-lg bg-cream p-3">
          <Field id="busy-label" name="label" label="What" placeholder="Soccer practice" required />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SelectField
              id="busy-weekday"
              name="weekday"
              label="Day"
              defaultValue="1"
              options={WEEKDAY_ORDER.map((d) => ({ value: String(d), label: WEEKDAY_LABEL[d] }))}
            />
            <Field
              id="busy-start"
              name="startTime"
              label="From"
              type="time"
              defaultValue="16:00"
              required
            />
            <Field
              id="busy-end"
              name="endTime"
              label="Until"
              type="time"
              defaultValue="17:30"
              required
            />
          </div>
          <SelectField
            id="busy-member"
            name="userId"
            label="Who (optional)"
            defaultValue=""
            options={[
              { value: '', label: 'Whole household' },
              ...(members.data?.members ?? []).map((m) => ({
                value: m.user.id,
                label: m.user.displayName,
              })),
            ]}
          />
          {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}
          <Button type="submit" variant="secondary" disabled={add.isPending}>
            Add busy time
          </Button>
        </form>
      ) : null}
    </Section>
  );
}

// ------------------------------------------------------------- thaw time --

function ThawSection({ canManage }: { canManage: boolean }) {
  const { auth, refresh } = useAuth();
  const household = auth.status === 'authed' ? auth.household : null;
  const [hours, setHours] = useState<string>('');
  useEffect(() => {
    if (household) setHours(String(household.thawLeadHours));
  }, [household]);
  const save = useMutation({
    mutationFn: (thawLeadHours: number) => api.households.update({ thawLeadHours }),
    onSuccess: () => refresh(),
  });
  const n = Number(hours);
  const valid = Number.isInteger(n) && n >= 1 && n <= 168;

  return (
    <Section
      icon={<BellIcon className="size-5" />}
      title="Thaw reminders"
      description="How long before a meal FewerBags suggests moving frozen ingredients to the fridge. Reminders that would land between 10 PM and 7 AM move to 8 PM the evening before."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) save.mutate(n);
        }}
      >
        <Field
          id="thaw-lead"
          label="Hours before serving"
          type="number"
          inputMode="numeric"
          min={1}
          max={168}
          value={hours}
          onChange={(e) => {
            save.reset();
            setHours(e.target.value);
          }}
          error={hours && !valid ? 'Between 1 and 168 hours' : undefined}
          disabled={!canManage}
        />
        <p className="text-xs text-muted">
          24 hours suits most meat and poultry; large roasts or a whole turkey need longer.
        </p>
        {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
        {save.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
        {canManage ? (
          <Button type="submit" variant="secondary" disabled={!valid || save.isPending}>
            Save lead time
          </Button>
        ) : null}
      </form>
    </Section>
  );
}

// ----------------------------------------------------- imported calendars --

function CalendarsSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const feeds = useQuery({ queryKey: scheduleKeys.feeds, queryFn: () => api.calendar.feeds() });
  const [helpOpen, setHelpOpen] = useState(false);
  const [removing, setRemoving] = useState<CalendarFeedDto | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: scheduleKeys.feeds });

  const add = useMutation({
    mutationFn: (input: { label: string; url: string }) => api.calendar.addFeed(input),
    onSuccess: invalidate,
  });
  const sync = useMutation({
    mutationFn: (id: string) => api.calendar.syncFeed(id),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: (f: CalendarFeedDto) => api.calendar.updateFeed(f.id, { enabled: !f.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.calendar.removeFeed(id),
    onSuccess: async () => {
      setRemoving(null);
      await invalidate();
    },
  });

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    add.mutate(
      { label: String(fd.get('label') ?? ''), url: String(fd.get('url') ?? '') },
      { onSuccess: () => form.reset() },
    );
  }

  const list = feeds.data?.feeds ?? [];
  return (
    <Section
      icon={<PlanIcon className="size-6" />}
      title="Connected calendars"
      description="Add your family calendar and FewerBags treats its events as busy time when planning the week. It only reads the calendar; nothing is ever written back."
    >
      {feeds.isError ? <Alert tone="error">{messageForError(feeds.error)}</Alert> : null}
      {sync.isError ? <Alert tone="error">{messageForError(sync.error)}</Alert> : null}
      {toggle.isError ? <Alert tone="error">{messageForError(toggle.error)}</Alert> : null}
      {list.length ? (
        <ul className="flex flex-col divide-y divide-line">
          {list.map((f) => (
            <li key={f.id} className="flex flex-col gap-2 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {f.label}
                    {f.enabled ? null : <span className="text-muted"> (paused)</span>}
                  </p>
                  <p className="truncate text-xs text-muted">{f.urlHint}</p>
                  <p className="text-xs text-muted">
                    {syncedAgo(f.lastSyncedAt)} · {f.eventCount} busy{' '}
                    {f.eventCount === 1 ? 'event' : 'events'} in the next 5 weeks
                  </p>
                </div>
              </div>
              {f.lastError ? <Alert tone="warning">Last sync failed: {f.lastError}</Alert> : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={smallButton}
                  disabled={sync.isPending && sync.variables === f.id}
                  onClick={() => sync.mutate(f.id)}
                >
                  {sync.isPending && sync.variables === f.id ? 'Syncing…' : 'Sync now'}
                </button>
                {canManage ? (
                  <>
                    <button
                      type="button"
                      className={smallButton}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate(f)}
                    >
                      {f.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button
                      type="button"
                      className={`${smallButton} text-fail`}
                      onClick={() => setRemoving(f)}
                    >
                      Remove
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : feeds.isSuccess ? (
        <p className="text-sm text-muted">No calendars connected yet.</p>
      ) : null}

      {canManage ? (
        <form onSubmit={onAdd} className="flex flex-col gap-3 rounded-lg bg-cream p-3">
          <div className="text-sm">
            <p className="font-medium">Add a Google calendar</p>
            <button
              type="button"
              className="text-brand underline underline-offset-2"
              aria-expanded={helpOpen}
              onClick={() => setHelpOpen((o) => !o)}
            >
              {helpOpen ? 'Hide the steps' : 'Where do I find the address?'}
            </button>
          </div>
          {helpOpen ? (
            <ol className="ml-5 list-decimal space-y-1 text-sm text-ink">
              <li>
                On a computer, open{' '}
                <a
                  href="https://calendar.google.com/calendar/r/settings"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand underline underline-offset-2"
                >
                  Google Calendar settings
                </a>
                .
              </li>
              <li>
                In the left column under “Settings for my calendars”, click the calendar you want
                (for example your family calendar).
              </li>
              <li>
                Scroll down to “Integrate calendar” and copy the{' '}
                <strong>Secret address in iCal format</strong> (it ends in <code>basic.ics</code>
                ).
              </li>
              <li>Paste it below and give it a name. Repeat for each calendar you want to add.</li>
              <li className="text-muted">
                Treat the address like a password — anyone with it can see the calendar. FewerBags
                keeps it private and never shows it again. Apple and Outlook calendars work too: use
                their “public” or “ICS” calendar link.
              </li>
            </ol>
          ) : null}
          <Field id="feed-label" name="label" label="Name" placeholder="Family" required />
          <Field
            id="feed-url"
            name="url"
            label="Secret iCal address"
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
            required
          />
          {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}
          <Button type="submit" variant="secondary" disabled={add.isPending}>
            {add.isPending ? 'Checking the calendar…' : 'Connect calendar'}
          </Button>
        </form>
      ) : null}

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove this calendar?"
      >
        <p className="text-sm">
          FewerBags will forget “{removing?.label}” and its busy times. You can add it again later.
        </p>
        {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => removing && remove.mutate(removing.id)}
          >
            Remove
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}

// ------------------------------------------------------------ export feed --

function ExportSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: scheduleKeys.exportStatus,
    queryFn: () => api.calendar.exportStatus(),
  });
  const [link, setLink] = useState<ExportTokenResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const create = useMutation({
    mutationFn: () => api.calendar.createExportToken(),
    onSuccess: (data) => {
      setLink(data);
      setConfirmRotate(false);
      qc.setQueryData(scheduleKeys.exportStatus, { enabled: true });
    },
  });
  const disable = useMutation({
    mutationFn: () => api.calendar.disableExport(),
    onSuccess: () => {
      setLink(null);
      qc.setQueryData(scheduleKeys.exportStatus, { enabled: false });
    },
  });

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const enabled = status.data?.enabled ?? false;
  return (
    <Section
      icon={<ArrowRightIcon className="size-5" />}
      title="Show FewerBags in Google Calendar"
      description="A private, read-only calendar of your planned meals and reminders that Google Calendar, Apple Calendar or Outlook can subscribe to."
    >
      {status.isError ? <Alert tone="error">{messageForError(status.error)}</Alert> : null}
      {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}
      {disable.isError ? <Alert tone="error">{messageForError(disable.error)}</Alert> : null}

      {link ? (
        <div className="flex flex-col gap-3">
          <Alert tone="success">
            Your calendar link is ready. Copy it now — for your privacy FewerBags shows it only
            once.
          </Alert>
          <div className="flex flex-col gap-1">
            <label htmlFor="export-url" className="text-sm font-medium">
              Calendar address
            </label>
            <div className="flex gap-2">
              <input
                id="export-url"
                readOnly
                value={link.webcalUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="min-h-11 min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 text-sm"
              />
              <button type="button" className={smallButton} onClick={() => copy(link.webcalUrl)}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <a
            href={link.googleUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-brand px-4 font-semibold text-white hover:bg-brand-dark"
          >
            Add to Google Calendar
          </a>
          <a
            href={link.webcalUrl}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-line bg-surface px-4 font-semibold text-brand hover:bg-brand-soft"
          >
            Open in Apple Calendar or Outlook
          </a>
        </div>
      ) : enabled ? (
        <p className="text-sm">
          A calendar link is active. FewerBags can’t show it again; make a new link if you need to
          add it somewhere else (the old one then stops working).
        </p>
      ) : status.isSuccess ? (
        <p className="text-sm text-muted">Not set up yet.</p>
      ) : null}

      <p className="text-xs text-muted">
        Google refreshes subscribed calendars only every few hours (sometimes up to a day), so a
        meal you just planned can take a while to appear. For a reminder that has to show up right
        away, use the “Add to Google Calendar” button next to it on the meal’s page.
      </p>

      {canManage ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          {!enabled ? (
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              Create calendar link
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => setConfirmRotate(true)}
                disabled={create.isPending}
              >
                Make a new link
              </Button>
              <Button
                variant="danger"
                onClick={() => disable.mutate()}
                disabled={disable.isPending}
              >
                Turn off
              </Button>
            </>
          )}
        </div>
      ) : null}

      <Dialog
        open={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        title="Make a new calendar link?"
      >
        <p className="text-sm">
          The current link stops working straight away. Calendars subscribed to it will need the new
          one.
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setConfirmRotate(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            Make a new link
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}
